import type { TimerCueEvent } from "@heraion/ui";
import { getRestAudioPreference } from "@heraion/ui";

// HI-3 slice 2b, web half — the web twin of apps/mobile/src/util/timerCue.ts.
// Same seam (`playTimerCue`), same shared hook deciding when; the difference is
// the mechanism, as it is for every other field of LoggerHostDeps.
//
// ⭐ This is the first sound the web logger has ever made. Mobile routes its
// cues through a notification channel because that is the only Android stream
// that ducks music instead of pausing it; the browser has no such problem, so
// the cue is synthesised here — two oscillator blips, no asset to download, no
// dependency to pin, and no autoplay policy to fight beyond the one gesture
// that started the timer in the first place.
//
// Because there is no notifee on web, this player owns the whole audible cue
// set: the 3-2-1 and BOTH phase starts. Mobile skips `phaseStart: 'rest'`
// (its scheduled bell already speaks at that instant); here nothing else does,
// so rest starting gets its own falling two-tone against work's rising one.
// That is `interval-training.md §9.4`'s "cue distinto lavoro/recupero", and on
// this platform it costs nothing.
//
// ⛔ `phaseEnd` plays for REST only, and the asymmetry is not a shortcut. Work
// ending is followed within a tick by rest (or by the next bout) starting, and
// that cue says the useful half — what to do now. Rest ending is followed by
// nothing at all, because the athlete starts the next work themselves
// (§10 decision 1: "il crono lo fa partire l'ATLETA"). Without this the web
// would count 3-2-1 and then go quiet one second before the moment that
// matters.

// The buzz, in the same two shapes mobile uses: a tick is one short tap, work
// starting is a longer double. `navigator.vibrate` exists on Android browsers
// and nowhere else, which is exactly where it is wanted — the web IS the
// non-Android channel, but it is also how an Android phone reaches JL without
// the APK. Everywhere else it is absent and this is a no-op.
const TICK_BUZZ = 40;
const WORK_BUZZ = [0, 90, 90, 160];

function buzz(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Some browsers throw instead of returning false when the page is not
    // visible. A cue that cannot buzz still has its sound.
  }
}

type Blip = { hz: number; ms: number };

// Rising = go, falling = ease off. The direction carries the meaning even when
// the two are heard seconds apart, which is the point: the athlete is not
// comparing them, they are recognising one.
const WORK_START: Blip[] = [
  { hz: 700, ms: 100 },
  { hz: 1050, ms: 160 },
];
const REST_START: Blip[] = [
  { hz: 1050, ms: 100 },
  { hz: 660, ms: 180 },
];
const LEAD_IN: Blip[] = [{ hz: 1050, ms: 80 }];

// Comfortably audible over a gym playlist without being a shock at 6am. Peak
// gain, shaped by the envelope below — a raw square-edged tone at this level
// would click.
const PEAK_GAIN = 0.22;
const ATTACK_S = 0.005;
const RELEASE_S = 0.04;

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  // Safari still ships it prefixed; `unknown` rather than `any` so the cast is
  // visible instead of silent.
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    // A browser that refuses to build a context (privacy mode, exotic policy)
    // gets the silent timer it had before this file existed.
    return null;
  }
  return ctx;
}

function blip(c: AudioContext, at: number, { hz, ms }: Blip): number {
  const dur = ms / 1000;
  const osc = c.createOscillator();
  const gain = c.createGain();
  // Sine: the softest waveform available, and the one least likely to sound
  // like an error beep on a laptop speaker.
  osc.type = "sine";
  osc.frequency.setValueAtTime(hz, at);
  // A ramped envelope, not a gate. Starting and stopping an oscillator at full
  // amplitude produces an audible click on every tick.
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(PEAK_GAIN, at + ATTACK_S);
  gain.gain.setValueAtTime(PEAK_GAIN, at + Math.max(ATTACK_S, dur - RELEASE_S));
  gain.gain.linearRampToValueAtTime(0, at + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + dur);
  return at + dur;
}

function play(blips: Blip[]): void {
  const c = audioContext();
  if (!c) return;
  // A context created before any gesture starts suspended. Every cue this file
  // plays is downstream of a tap that started a timer, so resuming here always
  // has a gesture behind it — but it is async, and the schedule below must not
  // wait for it, or the first tick of a lead-in would land late.
  if (c.state === "suspended") void c.resume();
  // ⭐ Scheduled against the AUDIO clock, not setTimeout. The audio thread is
  // not throttled when the tab loses focus, so a two-tone cue stays a two-tone
  // cue on a backgrounded tab instead of collapsing into one blip.
  let at = c.currentTime;
  for (const b of blips) at = blip(c, at, b);
}

/** The host half of the `playTimerCue` seam. Honours the athlete's two audio
 *  preferences separately, exactly as mobile does: 'none' silences the sound
 *  and leaves the buzz, vibrate off leaves the sound. The two toggles are
 *  documented as orthogonal (restSoundPreference.ts) and reading one through
 *  the other would collapse them. */
export function playTimerCue(cue: TimerCueEvent): void {
  const prefs = getRestAudioPreference();
  const sound = (blips: Blip[]) => {
    if (prefs.soundId !== "none") play(blips);
  };
  if (cue.kind === "leadIn") {
    if (prefs.vibrate) buzz(TICK_BUZZ);
    sound(LEAD_IN);
    return;
  }
  if (cue.kind === "phaseEnd") {
    if (cue.phase !== "rest") return;
    if (prefs.vibrate) buzz(WORK_BUZZ);
    sound(WORK_START);
    return;
  }
  if (prefs.vibrate) buzz(WORK_BUZZ);
  sound(cue.phase === "work" ? WORK_START : REST_START);
}
